/**
 * Rescues password-recovery links that land on the wrong page.
 *
 * Supabase mails a link to its own /auth/v1/verify endpoint. That endpoint
 * validates the one-time token in whatever browser opened it, then redirects to
 * `redirect_to` — but only when that value is on the project's allow list.
 * Otherwise it quietly falls back to the project's Site URL, which here is the
 * marketing landing page. So the recovery credentials arrive as
 *
 *   https://wegathered.online/#access_token=…&refresh_token=…&type=recovery
 *
 * on a page that has no password form. Two separate pieces of this app then
 * treated that fragment as an ordinary sign-in — `detectSessionInUrl` in the
 * Supabase client, and step 2 of AuthProvider's init — so the token was spent
 * silently and the user was left on the landing page, logged in, with no way to
 * finish the reset.
 *
 * This module moves such links to the page that can complete them, and snapshots
 * the credentials so whoever gets there can still read them.
 *
 * It must run before the Supabase client is constructed, which is why it is
 * imported at the top of lib/supabase.js rather than called from a component:
 * `detectSessionInUrl` strips the fragment during client init, long before any
 * lazily-loaded route mounts.
 */

/** Canonical page that completes a recovery. */
export const RESET_PATH = "/auth/reset-password";

/** Both spellings are routed in App.js, so neither should be redirected again. */
const RESET_PATHS = new Set(["/auth/reset-password", "/reset-password"]);

/**
 * Credentials lifted out of the fragment before anything could consume them.
 * Module-level rather than passed around: the reader is a lazily-loaded route
 * that mounts long after this runs.
 */
let snapshot = null;

function parseFragment(hash) {
  if (!hash || hash.length < 2) return null;
  try {
    return new URLSearchParams(hash.replace(/^#/, ""));
  } catch {
    return null;
  }
}

/**
 * True when a fragment carries Supabase *recovery* credentials.
 *
 * Keyed on `type=recovery` and nothing else. Matching on the presence of an
 * access_token would also swallow OAuth returns and the mobile WebView handoff,
 * both of which legitimately sign the user in from the fragment.
 */
export function isRecoveryFragment(hash) {
  const params = parseFragment(hash);
  return params?.get("type") === "recovery";
}

/**
 * Read the recovery credentials captured at startup.
 *
 * Single-use: the caller exchanges them for a session, and leaving them in
 * memory afterwards only widens the window in which they could be picked up.
 *
 * @returns {null | { accessToken: string, refreshToken: string }}
 */
export function takeRecoveryCredentials() {
  const value = snapshot;
  snapshot = null;
  return value;
}

/**
 * Capture recovery credentials and, if this is the wrong page for them, hand the
 * link over to the reset form with the fragment intact.
 *
 * @param {Location} location
 * @returns {boolean} true when a redirect was started
 */
export function routeRecoveryLink(location) {
  const { hash, pathname, search } = location;
  if (!isRecoveryFragment(hash)) return false;

  const params = parseFragment(hash);
  const accessToken = params.get("access_token") || "";
  const refreshToken = params.get("refresh_token") || "";
  if (accessToken && refreshToken) {
    snapshot = { accessToken, refreshToken };
  }

  // Already where it needs to be. The snapshot above is the useful part.
  if (RESET_PATHS.has(pathname)) return false;

  // replace() rather than assign(): the landing page was never a destination the
  // user chose, so it does not belong in their history.
  location.replace(`${RESET_PATH}${search || ""}${hash}`);
  return true;
}

// Runs on import, before createClient in lib/supabase.js.
if (typeof window !== "undefined" && window.location) {
  routeRecoveryLink(window.location);
}
